import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { 
  ArrowLeft,
  Search,
  BookOpen,
  Layers,
  MessageSquare,
  Calendar,
  StickyNote,
  Users,
  Bot,
  Settings,
  Zap,
  ChevronRight,
  ExternalLink,
  Home,
  Command,
  Keyboard,
  Play,
  FileText,
  HelpCircle,
  Lightbulb,
  Target,
  Workflow,
  Database,
  Shield,
  Puzzle,
  Video,
  Clock,
  Star,
  CheckCircle2,
  Grid3X3,
  MousePointer,
  Palette,
  Eye,
  Filter,
  FolderOpen,
  Bell,
  Mail,
  Save,
  RotateCcw,
  Move,
  Copy,
  Pin,
  Archive,
  Trash2,
  Link,
  Tag,
  CalendarDays,
  PlusCircle,
  Edit3,
  Layout,
  Monitor,
  Smartphone,
  Check,
  X,
  AlertTriangle,
  Info,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Code,
  Terminal,
  Github,
  Globe,
  Wifi,
  WifiOff,
  RefreshCw
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'

export default function Documentation() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const quickStart = [
    {
      title: "Create Your First Workspace",
      description: "Set up a workspace with the panel system",
      time: "2 min",
      steps: [
        "Navigate to /workspace - Krushr redirects to your last workspace or creates one",
        "Use the workspace selector in the header to create new workspaces",
        "Invite team members through the team management system",
        "Configure workspace preferences in the sidebar"
      ]
    },
    {
      title: "Master the Panel System",
      description: "Learn Krushr's unique drag-and-drop workspace",
      time: "3 min", 
      steps: [
        "Use ⌘+Shift+K to create new Kanban panels",
        "Drag panels around the workspace to organize your layout",
        "Resize panels by dragging their edges",
        "Use ⌘+S to save your panel layout for later"
      ]
    },
    {
      title: "Start with AI-Powered Features",
      description: "Leverage Krushr's AI integration for productivity",
      time: "2 min",
      steps: [
        "Press ⌘+Shift+A to open the AI Assistant panel",
        "Use natural language to create tasks: 'Create task for user testing'",
        "Ask AI to analyze your workspace performance",
        "Set up AI-powered task suggestions and scheduling"
      ]
    }
  ]

  const featureGuides = [
    {
      category: "Panel-Based Workspace",
      icon: Layout,
      color: "bg-krushr-primary",
      guides: [
        { 
          title: "Understanding Panels", 
          description: "Krushr's core concept: draggable, resizable content containers",
          details: "PanelWorkspace component manages grid-based layouts. Panels can be KANBAN, NOTES, CALENDAR, CHAT, or AI_CHAT types."
        },
        { 
          title: "Panel Management", 
          description: "Create, resize, move, and organize panels in your workspace",
          details: "Use the panel creation buttons in WorkspaceHeaderConsolidated. Drag panels to reposition, resize by edges."
        },
        { 
          title: "Layout Persistence", 
          description: "Save and load custom workspace layouts",
          details: "Layout system stores panel positions and sizes. Use ⌘+S to save, load through LayoutManagerModal."
        },
        { 
          title: "Focus Mode", 
          description: "Minimize distractions with single-panel focus",
          details: "UIStore focusMode toggles panel visibility. Press ⌘+Shift+F or click panel focus button."
        }
      ]
    },
    {
      category: "Kanban Task Management", 
      icon: Layers,
      color: "bg-green-500",
      guides: [
        { 
          title: "Board Architecture", 
          description: "Columns, tasks, and hierarchical organization",
          details: "KanbanBoard component with drag-drop. Tasks have parent/child relationships, priorities, and custom fields."
        },
        { 
          title: "Task Properties", 
          description: "Rich task data including due dates, assignees, and tags",
          details: "Task model includes description, dueDate, priority (LOW/MEDIUM/HIGH/CRITICAL), assignee, and custom tags."
        },
        { 
          title: "Real-time Collaboration", 
          description: "Live updates via WebSocket connections",
          details: "useRealtimeConnection hook manages WebSocket. Task changes broadcast instantly to all connected users."
        },
        { 
          title: "Bulk Operations", 
          description: "Efficient multi-task editing and organization",
          details: "Select multiple tasks with checkboxes. Bulk actions include status changes, assignee updates, and archiving."
        }
      ]
    },
    {
      category: "Advanced Notes System",
      icon: StickyNote, 
      color: "bg-purple-500",
      guides: [
        { 
          title: "Smart Note Editor", 
          description: "Markdown support with live preview and AI suggestions",
          details: "IntelligentEditor component with ReactMarkdown. Auto-suggests titles based on content analysis."
        },
        { 
          title: "Color & Organization", 
          description: "Visual organization with colors, pinning, and folders",
          details: "9 color system (blue, green, yellow, red, purple, orange, pink, gray, white) with ColorPicker component."
        },
        { 
          title: "Quick Creation", 
          description: "Templates and smart search for rapid note creation",
          details: "NOTE_TEMPLATES include Meeting Notes, Daily Journal, Project Ideas. Intelligent search suggests existing patterns."
        },
        { 
          title: "Content Linking", 
          description: "Link notes to tasks and other workspace content",
          details: "Notes can reference tasks through workspace context. WorkspaceContextStore manages entity relationships."
        }
      ]
    },
    {
      category: "AI-Powered Features",
      icon: Bot,
      color: "bg-gradient-to-r from-krushr-primary to-krushr-secondary",
      guides: [
        { 
          title: "Workspace AI Chat", 
          description: "Context-aware AI assistant for project management",
          details: "WorkspaceAiChat component with conversation history. AI understands workspace context and can create tasks."
        },
        { 
          title: "Natural Language Processing", 
          description: "Create tasks from plain English descriptions",
          details: "AI Task Parser router processes natural language into structured task data with priorities and due dates."
        },
        { 
          title: "Smart Suggestions", 
          description: "AI-powered recommendations for productivity",
          details: "AI analyzes workspace patterns to suggest task assignments, deadlines, and workflow optimizations."
        },
        { 
          title: "Performance Analytics", 
          description: "AI insights into team productivity and bottlenecks",
          details: "AI router provides analytics on task completion rates, team performance, and workflow efficiency."
        }
      ]
    },
    {
      category: "Advanced Calendar",
      icon: Calendar,
      color: "bg-orange-500", 
      guides: [
        { 
          title: "Responsive Calendar Views", 
          description: "Adaptive layout for any panel size",
          details: "NewCalendarPanel with micro/small/medium/large responsive layouts. Automatic UI adaptation based on panel dimensions."
        },
        { 
          title: "Event Management", 
          description: "Rich event types with priorities and locations",
          details: "CalendarEvent types: MEETING, TASK, REMINDER, EVENT, DEADLINE, MILESTONE with color coding and priority levels."
        },
        { 
          title: "Holiday Integration", 
          description: "Built-in US federal holiday support",
          details: "Comprehensive 2024-2025 US federal holidays with visual indicators and tooltips for holiday information."
        },
        { 
          title: "Task Integration", 
          description: "Seamless connection between calendar and task management",
          details: "Calendar events link to kanban tasks. CompactTaskModal integrates calendar mode for deadline management."
        }
      ]
    },
    {
      category: "System Architecture",
      icon: Database,
      color: "bg-indigo-500",
      guides: [
        { 
          title: "tRPC API System", 
          description: "Type-safe API with 29 specialized routers",
          details: "Full-stack type safety with auth, workspace, task, calendar, AI, and 24 other specialized API routers."
        },
        { 
          title: "State Management", 
          description: "Zustand stores for UI, auth, and workspace context",
          details: "UIStore, AuthStore, WorkspaceContextStore, CommentsStore, UndoStore for comprehensive state management."
        },
        { 
          title: "Real-time Architecture", 
          description: "WebSocket connections for live collaboration",
          details: "useRealtimeConnection manages persistent WebSocket. Real-time updates for tasks, comments, and workspace changes."
        },
        { 
          title: "Database Schema", 
          description: "Prisma ORM with PostgreSQL for complex relationships",
          details: "Rich relational schema: User, Workspace, Team, Project, Kanban, Task hierarchy, Comments, Calendar, AI conversations."
        }
      ]
    }
  ]

  const troubleshooting = [
    {
      category: "Panel System Issues",
      items: [
        { 
          q: "Panels not saving their position after refresh", 
          a: "Use ⌘+S to save your layout. Check that localStorage is enabled and you're logged in. The layout system persists to the database via the layout router." 
        },
        { 
          q: "Can't drag or resize panels", 
          a: "Ensure you're not in focus mode (⌘+Shift+F to toggle). Check that the PanelWorkspace component isn't in a loading state. Try refreshing if panels appear frozen." 
        },
        { 
          q: "Panels appear overlapped or incorrectly sized", 
          a: "This can happen with saved layouts from different screen sizes. Use 'Load Layout' in the header menu and select 'Reset to Default' to clear corrupted layout data." 
        },
        { 
          q: "New panels aren't appearing", 
          a: "Check the panel creation buttons in WorkspaceHeaderConsolidated. Ensure your workspace ID is valid and you have creation permissions." 
        }
      ]
    },
    {
      category: "Real-time Collaboration",
      items: [
        { 
          q: "Task changes not appearing for team members", 
          a: "Check the WebSocket connection status. The useRealtimeConnection hook manages this. Look for connection indicators in the workspace header." 
        },
        { 
          q: "Comments and mentions not notifying users", 
          a: "Verify notification permissions in browser settings. Check that the notification router is working and users have proper workspace access." 
        },
        { 
          q: "Seeing 'disconnected' status frequently", 
          a: "This indicates unstable WebSocket connection. Check your network stability. The system auto-reconnects but may need a manual refresh." 
        }
      ]
    },
    {
      category: "Performance & Loading",
      items: [
        { 
          q: "Workspace takes long to load with many panels", 
          a: "Each panel makes separate tRPC queries. Use focus mode (⌘+Shift+F) to hide unused panels. Consider saving simpler layouts for faster loading." 
        },
        { 
          q: "Calendar or notes panels loading slowly", 
          a: "These components have responsive layouts that calculate on resize. For better performance, keep panels at consistent sizes and minimize frequent resizing." 
        },
        { 
          q: "AI chat responses are slow", 
          a: "AI responses depend on external API calls. Check your internet connection. The WorkspaceAiChat component caches responses for better performance." 
        }
      ]
    },
    {
      category: "Data & Synchronization",
      items: [
        { 
          q: "Tasks disappeared after creating them", 
          a: "Check that the task creation completed successfully. Look for error toasts from Sonner. Verify the CompactTaskModal saved properly and try refreshing." 
        },
        { 
          q: "Notes not saving automatically", 
          a: "The IntelligentEditor auto-saves every 1000ms. If it's not working, check your connection and try manually saving with ⌘+S." 
        },
        { 
          q: "Search not finding recent content", 
          a: "The search router may need time to index new content. tRPC queries cache for performance - try clearing the search query and re-typing." 
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Consistent with Krushr design */}
      <header className="krushr-header-base krushr-header-workspace px-4 sm:px-6 lg:px-8 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Workspace</span>
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-krushr-primary to-krushr-secondary flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Krushr Documentation</h1>
                  <p className="text-xs text-gray-500">Everything you need to master Krushr</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 krushr-header-search"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://github.com/krushr-project/krushr/discussions', '_blank')}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Community
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Master Your Workspace
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Comprehensive guides, tutorials, and best practices to help you get the most out of Krushr's powerful project management platform.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button className="bg-krushr-primary hover:bg-krushr-primary/90 text-white px-6 py-3">
              <Play className="w-4 h-4 mr-2" />
              Quick Start Guide
            </Button>
            <Button variant="outline" className="px-6 py-3">
              <Video className="w-4 h-4 mr-2" />
              Watch Tutorial
            </Button>
          </div>
        </div>

        <Tabs defaultValue="guides" className="space-y-8">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl mx-auto">
            <TabsTrigger value="guides" className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4" />
              <span>Feature Guides</span>
            </TabsTrigger>
            <TabsTrigger value="quickstart" className="flex items-center space-x-2">
              <Play className="w-4 h-4" />
              <span>Quick Start</span>
            </TabsTrigger>
            <TabsTrigger value="shortcuts" className="flex items-center space-x-2">
              <Keyboard className="w-4 h-4" />
              <span>Shortcuts</span>
            </TabsTrigger>
            <TabsTrigger value="help" className="flex items-center space-x-2">
              <HelpCircle className="w-4 h-4" />
              <span>Help</span>
            </TabsTrigger>
          </TabsList>

          {/* Feature Guides */}
          <TabsContent value="guides" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featureGuides.map((category) => {
                const Icon = category.icon
                return (
                  <div key={category.category} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{category.category}</h3>
                    </div>
                    <div className="space-y-3">
                      {category.guides.map((guide, index) => (
                        <button
                          key={index}
                          className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900 group-hover:text-krushr-primary">
                                {guide.title}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">{guide.description}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-krushr-primary" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </TabsContent>

          {/* Quick Start */}
          <TabsContent value="quickstart" className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Started in Minutes</h3>
              <p className="text-gray-600">Follow these step-by-step guides to set up your workspace and start collaborating.</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {quickStart.map((guide, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-krushr-primary text-white flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{guide.title}</h4>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{guide.time}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{guide.description}</p>
                  <ol className="space-y-2">
                    {guide.steps.map((step, stepIndex) => (
                      <li key={stepIndex} className="flex items-start space-x-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                  <Button className="w-full mt-4 bg-krushr-primary hover:bg-krushr-primary/90">
                    Start Guide
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Keyboard Shortcuts */}
          <TabsContent value="shortcuts" className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Keyboard Shortcuts</h3>
              <p className="text-gray-600">Master Krushr's keyboard shortcuts to work faster and more efficiently.</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <Command className="w-4 h-4 mr-2" />
                    Essential
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: '⌘K', desc: 'Command palette' },
                      { key: '/', desc: 'Search calendar' },
                      { key: 'T', desc: 'Go to today' },
                      { key: 'Esc', desc: 'Close modals' }
                    ].map(shortcut => (
                      <div key={shortcut.key} className="flex items-center justify-between py-1">
                        <span className="text-sm text-gray-600">{shortcut.desc}</span>
                        <Badge variant="outline" className="font-mono text-xs">{shortcut.key}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <Layers className="w-4 h-4 mr-2" />
                    Panels
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: '⌘⇧K', desc: 'New kanban panel' },
                      { key: '⌘⇧N', desc: 'New notes panel' },
                      { key: '⌘⇧C', desc: 'New calendar panel' },
                      { key: '⌘⇧A', desc: 'New AI chat panel' }
                    ].map(shortcut => (
                      <div key={shortcut.key} className="flex items-center justify-between py-1">
                        <span className="text-sm text-gray-600">{shortcut.desc}</span>
                        <Badge variant="outline" className="font-mono text-xs">{shortcut.key}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    Workspace
                  </h4>
                  <div className="space-y-2">
                    {[
                      { key: '⌘S', desc: 'Save panel layout' },
                      { key: '⌘⇧F', desc: 'Toggle focus mode' },
                      { key: '⌘←/→', desc: 'Navigate calendar' },
                      { key: 'M/A', desc: 'Calendar views' }
                    ].map(shortcut => (
                      <div key={shortcut.key} className="flex items-center justify-between py-1">
                        <span className="text-sm text-gray-600">{shortcut.desc}</span>
                        <Badge variant="outline" className="font-mono text-xs">{shortcut.key}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Help & Troubleshooting */}
          <TabsContent value="help" className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Help & Troubleshooting</h3>
              <p className="text-gray-600">Common questions and solutions to keep you productive.</p>
            </div>

            <div className="space-y-6">
              {troubleshooting.map((section) => (
                <div key={section.category} className="bg-white rounded-xl border border-gray-200 p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{section.category}</h4>
                  <div className="space-y-4">
                    {section.items.map((item, index) => (
                      <details key={index} className="group">
                        <summary className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:bg-gray-50">
                          <span className="font-medium text-gray-900">{item.q}</span>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                        </summary>
                        <div className="mt-2 p-3 text-gray-600 bg-gray-50 rounded-lg">
                          {item.a}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Support Contact */}
            <div className="bg-gradient-to-r from-krushr-primary to-krushr-secondary rounded-xl p-8 text-white text-center">
              <h4 className="text-xl font-semibold mb-2">Still Need Help?</h4>
              <p className="mb-6 opacity-90">Our team is here to help you succeed with Krushr.</p>
              <div className="flex items-center justify-center space-x-4">
                <Button variant="secondary" className="bg-white text-krushr-primary hover:bg-white/90">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.open('https://github.com/krushr-project/krushr/discussions', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Community Forum
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}